// @ts-check
// Chamadas das campanhas e comunicados de WhatsApp (contrato 09 de campanhas de WhatsApp, seção 13): lista, rascunho,
// pré-checagem, envio, agenda, pausa, relatório, destinatários, teste e o WhatsApp para testes do membro.
import { api, requestFile } from './api-client.js';

/**
 * @typedef {{ recipients: number, accepted: number, delivered: number, read: number, replied: number, failed: number,
 *   skipped?: number, optedOut?: number, queued?: number, sent?: number, held?: number, billable?: number, billableMarketing?: number,
 *   serviceWindow?: number, audienceCards?: number, duplicates?: number }} CampaignCounts
 * @typedef {{ id: string, name: string, kind: 'MARKETING' | 'ANNOUNCEMENT', status: string, waitingReason: string | null, pauseReason: string | null,
 *   nextStepAt?: string | null, channel: { id: string, label: string } | null, template: { id: string, name: string, category: string | null } | null,
 *   counts: CampaignCounts, scheduledAt: string | null, startedAt: string | null, finishedAt: string | null, updatedAt: string }} WhatsappCampaignSummary
 * @typedef {WhatsappCampaignSummary & { variableMappings: import('../../../shared/whatsapp/template-variables.js').VariableMappings,
 *   audienceDefinition: any, audienceOwner: { workspaceMemberId: string, name: string } | null,
 *   options: { sendWindow: { enabled: boolean }, frequencyCapHours: number }, mediaCampaign: { id: string, name: string } | null,
 *   marketingAppliedSince: string | null, pauseDetails: any, failureReason: string | null, preparedAt: string | null, recipientsPurged: boolean,
 *   canEdit: boolean, canStart: boolean }} WhatsappCampaign
 * @typedef {{ code: string, [key: string]: any }} PrecheckNotice
 * @typedef {{ computedAt: string, kind: string, purposeApplied: 'MARKETING' | 'SERVICE',
 *   audience: { cards: number, isEstimate: boolean, eligible: number, duplicates: number, excluded: Record<string, number>, serviceWindowOpen: number,
 *     serviceWithoutRelationship: number, byCreatedBySource?: Record<string, number> },
 *   toSend: number, template: any, channel: { id: string, label: string, isActive: boolean } | null,
 *   sample: { to: { displayName: string, phone: string } | null, text: string, fallbackVariables: string[], problem: any } | null,
 *   sendWindow: { enabled: boolean, isOpenNow: boolean, nextStart: string | null }, frequencyCapHours: number | null,
 *   portfolio: { label: string, tier: string | null, limit: number | null, isLimitConfirmed: boolean, usedLast24h: number, cap: number | null,
 *     availableNow: number | null, estimatedDays: number | null, nextSlotsAt: string | null } | null,
 *   cost: { isAvailable: boolean, currency?: string, unitPrice?: number, estimatedTotal?: number, referenceDate?: string | null },
 *   warnings: PrecheckNotice[], blockers: PrecheckNotice[] }} WhatsappCampaignPrecheck
 * @typedef {{ status: string, waitingReason: string | null, nextStepAt: string | null, pauseReason: string | null, pauseDetails: any,
 *   failureReason: string | null, counts: CampaignCounts, rates: Record<string, number | null>, progress: { processed: number, total: number },
 *   failuresByCode: Array<{ code: number | null, errorClass: string | null, count: number, canResend?: boolean, resendAvailableAt?: string | null }>,
 *   skippedByReason: Record<string, number>, cost: any, events: Array<{ kind: string, at: string, details: any, actorName: string | null }>,
 *   recipientsPurged: boolean, marketingAppliedSince: string | null, lastUpdatedAt: string }} WhatsappCampaignReport
 * @typedef {{ id: string, recordId: string | null, recordName: string | null, personId: string | null, displayName: string | null, phone: string | null,
 *   status: string, skipReason: string | null, errorCode: number | null, errorClass: string | null, purposeApplied: string | null,
 *   acceptedAt: string | null, deliveredAt: string | null, readAt: string | null, repliedAt: string | null, optedOutAt: string | null,
 *   failedAt: string | null, retryAfter: string | null, messageThreadId: string | null, isOutOfScope: boolean }} WhatsappCampaignRecipient
 * @typedef {{ phone: string | null, changedAt: string | null, canChangeAt: string | null, testsUsed: number, testsLimit: number, testsRemaining: number }} WhatsappTestPhone
 */

const BASE = '/rest/whatsapp-campaigns';
/** @param {string} campaignId */
const campaignPath = (campaignId) => `${BASE}/${encodeURIComponent(campaignId)}`;

/**
 * @param {{ status?: string, kind?: string, search?: string, limit?: number, startingAfter?: string }} [query]
 * @returns {Promise<{ campaigns: WhatsappCampaignSummary[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listWhatsappCampaigns(query = {}) {
  const body = await api.get(BASE, Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== '')));
  return body.data;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaign>} */
export async function getWhatsappCampaign(campaignId) {
  return (await api.get(campaignPath(campaignId))).data.campaign;
}

/** @param {Record<string, unknown>} input @returns {Promise<WhatsappCampaign>} */
export async function createWhatsappCampaign(input) {
  return (await api.post(BASE, input)).data.campaign;
}

/** @param {string} campaignId @param {Record<string, unknown>} input @returns {Promise<WhatsappCampaign>} */
export async function updateWhatsappCampaign(campaignId, input) {
  return (await api.patch(campaignPath(campaignId), input)).data.campaign;
}

/** @param {string} campaignId */
export async function deleteWhatsappCampaign(campaignId) {
  return (await api.delete(campaignPath(campaignId))).data.campaign;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaign>} */
export async function duplicateWhatsappCampaign(campaignId) {
  return (await api.post(`${campaignPath(campaignId)}/duplicate`, {})).data.campaign;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaignPrecheck>} */
export async function precheckWhatsappCampaign(campaignId) {
  return (await api.post(`${campaignPath(campaignId)}/precheck`, {})).data.precheck;
}

/**
 * @param {string} campaignId @param {{ acknowledgeMarketingCost?: boolean, acknowledgeLowQuality?: boolean }} [acknowledgements]
 * @returns {Promise<WhatsappCampaign>}
 */
export async function startWhatsappCampaign(campaignId, acknowledgements = {}) {
  return (await api.post(`${campaignPath(campaignId)}/start`, acknowledgements)).data.campaign;
}

/**
 * @param {string} campaignId @param {{ scheduledAt: string, acknowledgeMarketingCost?: boolean, acknowledgeLowQuality?: boolean }} input
 * @returns {Promise<WhatsappCampaign>}
 */
export async function scheduleWhatsappCampaign(campaignId, input) {
  return (await api.post(`${campaignPath(campaignId)}/schedule`, input)).data.campaign;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaign>} */
export async function unscheduleWhatsappCampaign(campaignId) {
  return (await api.post(`${campaignPath(campaignId)}/unschedule`, {})).data.campaign;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaign>} */
export async function pauseWhatsappCampaign(campaignId) {
  return (await api.post(`${campaignPath(campaignId)}/pause`, {})).data.campaign;
}

/**
 * @param {string} campaignId @param {{ acknowledgeLowQuality?: boolean, acknowledgeOptOutRate?: boolean }} [acknowledgements]
 * @returns {Promise<WhatsappCampaign>}
 */
export async function resumeWhatsappCampaign(campaignId, acknowledgements = {}) {
  return (await api.post(`${campaignPath(campaignId)}/resume`, acknowledgements)).data.campaign;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaign>} */
export async function cancelWhatsappCampaign(campaignId) {
  return (await api.post(`${campaignPath(campaignId)}/cancel`, {})).data.campaign;
}

/** @param {string} campaignId @returns {Promise<{ campaign: WhatsappCampaign, requeued: number }>} */
export async function resendLimitedWhatsappCampaign(campaignId) {
  return (await api.post(`${campaignPath(campaignId)}/resend-limited`, {})).data;
}

/**
 * @param {string} campaignId @param {{ cardId?: string | null }} [input]
 * @returns {Promise<{ status: string, wamid: string | null, errorCode: number | null, text: string, testsUsed: number, testsLimit: number }>}
 */
export async function sendWhatsappCampaignTest(campaignId, input = {}) {
  return (await api.post(`${campaignPath(campaignId)}/test`, input.cardId ? { cardId: input.cardId } : {})).data.test;
}

/** @param {string} campaignId @returns {Promise<WhatsappCampaignReport>} */
export async function getWhatsappCampaignReport(campaignId) {
  return (await api.get(`${campaignPath(campaignId)}/report`)).data.report;
}

/**
 * @param {string} campaignId @param {{ status?: string, limit?: number, startingAfter?: string | null }} [query]
 * @returns {Promise<{ recipients: WhatsappCampaignRecipient[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
 */
export async function listWhatsappCampaignRecipients(campaignId, query = {}) {
  return (await api.get(`${campaignPath(campaignId)}/recipients`, Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== '')))).data;
}

/** @param {string} campaignId */
export function downloadWhatsappCampaignRecipients(campaignId) {
  return requestFile(`${campaignPath(campaignId)}/recipients.csv`);
}

/** @returns {Promise<WhatsappTestPhone>} */
export async function getWhatsappTestPhone() {
  return (await api.get(`${BASE}/test-phone`)).data.testPhone;
}

/** @param {string} phone @returns {Promise<WhatsappTestPhone>} */
export async function setWhatsappTestPhone(phone) {
  return (await api.put(`${BASE}/test-phone`, { phone })).data.testPhone;
}

/**
 * Confirmação automática do pedido de saída de um número (Configurações > Canais, flag WORKSPACE).
 * @param {string} channelId @param {boolean} optOutConfirmation
 */
export async function updateWhatsappChannelSettings(channelId, optOutConfirmation) {
  return (await api.patch(`/rest/channels/${encodeURIComponent(channelId)}/whatsapp/settings`, { optOutConfirmation })).data.channel;
}
